import React, { useEffect, useState } from "react";
import { getAllMatches, deleteMatch } from "./services/matchService";
import { getPlayerFullName } from "./playerDisplay";
import { useSubscription } from "./hooks/useSubscription";  // 🆕 Import du hook
import UpgradeModal from "./UpgradeModal";  // 🆕 Import du modal d'upgrade
import { importMatchFromExcel } from "./services/excelImportService"; // 🆕 Import Excel

// 🆕 Fonction helper pour formater les dates
const formatDate = (dateString) => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    
    // Jour/Mois sur la première ligne
    const dayMonth = date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit'
    });
    
    // Année sur la deuxième ligne
    const year = date.getFullYear();
    
    return (
      <div style={{ fontSize: '13px', lineHeight: '1.3' }}>
        <div>{dayMonth}</div>
        <div style={{ color: '#666' }}>{year}</div>
      </div>
    );
  } catch (e) {
    return dateString;
  }
};

function MatchListPage({ currentUser, onLogout, onOpenMatch, onNewMatch, onEditMatch, onViewStats, onNavigateVideos, onNavigatePlayers }) {
  const [matches, setMatches] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedForExport, setSelectedForExport] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);  // 🆕 État pour le modal d'upgrade
  const [excelImporting, setExcelImporting] = useState(false); // 🆕 État import Excel

  // 🆕 Hook pour gérer les profils et limites
  const { 
    role,
    isGratuit,
    matchesCount,      // 🔧 AJOUTÉ
    matchesLimit,
    matchesRemaining,
    canCreateMatch,
    // hasExpertAccess,  // TODO: Utiliser pour le bouton Expert
    // hasAdminAccess,   // TODO: Utiliser pour les fonctionnalités admin
    loading: subscriptionLoading 
  } = useSubscription();

  // 🆕 Handler pour créer un nouveau match avec vérification des limites
  const handleNewMatch = () => {
  console.log('🔍 DEBUG useSubscription:');
  console.log('  role:', role);
  console.log('  matchesCount:', matchesCount);
  console.log('  matchesLimit:', matchesLimit);
  console.log('  matchesRemaining:', matchesRemaining);
  console.log('  canCreateMatch:', canCreateMatch);


    // Vérifier si l'utilisateur peut créer un match
    if (!canCreateMatch) {
      setShowUpgradeModal(true);  // 🆕 Afficher le modal au lieu de l'alert
      return;
    }
    
    // Créer le match
    onNewMatch();
  };

  useEffect(() => {
    async function loadMatches() {
      try {
        console.log('📋 Chargement des matchs...');
        
        // ✅ UTILISER getAllMatches pour charger depuis Supabase + localStorage
        const { matches: loadedMatches } = await getAllMatches();
        
        console.log(`📋 ${loadedMatches.length} matchs chargés`);
        
        if (loadedMatches.length > 0) {
          console.log('📋 Premier match:');
          console.log('  - ID:', loadedMatches[0].id);
          console.log('  - engineState?', !!loadedMatches[0].engineState);
          console.log('  - Points?', loadedMatches[0].engineState?.points?.length);
        }
        
        // ✅ Utiliser les matchs chargés (avec priorité Supabase)
        setMatches(loadedMatches);
        
      } catch (error) {
        console.error("❌ Erreur chargement matchs:", error);
      }
    }
    
    loadMatches();
  }, [currentUser.userId]);

// 🆕 Recharger les matchs quand l'app revient au premier plan (retour de veille)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ App revenue au premier plan, rechargement des matchs...');
        async function reload() {
          try {
            const { matches: reloaded } = await getAllMatches();
            setMatches(reloaded);
          } catch (error) {
            console.error("❌ Erreur rechargement matchs:", error);
          }
        }
        reload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);


    const handleDeleteMatch = async (matchId) => {
      try {
        console.log('🗑️ Suppression du match:', matchId);
        
        const result = await deleteMatch(matchId);
        
        // 🆕 Messages adaptés
        if (result.localOnly) {
          console.log('✅ Match local supprimé (pas dans le cloud)');
        } else if (result.offline) {
          alert('⚠️ Match supprimé localement\n(Sera supprimé du cloud quand vous aurez Internet)');
        } else if (result.notFound) {
          console.log('⚠️ Match non trouvé dans Supabase');
        } else if (result.online) {
          console.log('✅ Match supprimé (local + cloud)');
        }
        
        // Recharger la liste
        const updatedResult = await getAllMatches();
        setMatches(updatedResult.matches);
        
        setDeleteConfirm(null);
      } catch (error) {
        console.error("❌ Erreur suppression:", error);
        alert('❌ Erreur lors de la suppression');
        setDeleteConfirm(null);
      }
    };

    const handleEditMatch = (match) => {
      onEditMatch(match);
    };

    const getWinner = (currentScore, match = {}) => {
      if (!currentScore?.sets) return null;
      
      // 🆕 PRIORITÉ 1 : Winner manuel (abandon)
      if (currentScore.abandonWinner) {
        return currentScore.abandonWinner;
      }
      
      // 🆕 PRIORITÉ 2 : Winner calculé par TennisScoreEngine
      if (currentScore.winner) {
        return currentScore.winner;
      }
      
      // PRIORITÉ 3 : Calculer à partir des sets
      
      // Fonction pour vérifier si un set est RÉELLEMENT terminé
      const isSetCompleted = (set, tbConfirmed = false) => {
        if (!set || !Array.isArray(set)) return false;
        const [s1, s2] = set;
        
        // 🆕 SUPER TIE-BREAK (S10/T10)
        // Format 1: Score 1-0 ou 0-1 = set S10 terminé SEULEMENT si tbConfirmed
        if ((s1 === 1 && s2 === 0) || (s1 === 0 && s2 === 1)) {
          return tbConfirmed;
        }
        
        // Format 2: Score numérique du super TB (10-8, 11-9, etc.)
        if ((s1 >= 10 && s1 - s2 >= 2) || (s2 >= 10 && s2 - s1 >= 2)) {
          return true;
        }
        
        // SETS NORMAUX
        return (
          (s1 >= 6 && s1 - s2 >= 2) ||    // 6-0, 6-1, 6-2, 6-3, 6-4, 7-5, 8-6...
          (s2 >= 6 && s2 - s1 >= 2) ||    // Idem pour J2
          (s1 === 7 && s2 === 6) ||       // 7-6 (tie-break)
          (s2 === 7 && s1 === 6)          // 7-6 (tie-break)
        );
      };
      
      // Compter SEULEMENT les sets TERMINÉS
      const lastTBScores = currentScore.lastTieBreakScores || [];
      const j1SetsWon = currentScore.sets.filter((set, i) => {
        const tb = lastTBScores[i];
        const tbConfirmed = tb && Array.isArray(tb) &&
          ((tb[0] >= 10 && tb[0] - tb[1] >= 2) || (tb[1] >= 10 && tb[1] - tb[0] >= 2));
        return isSetCompleted(set, tbConfirmed) && set[0] > set[1];
      }).length;
      const j2SetsWon = currentScore.sets.filter((set, i) => {
        const tb = lastTBScores[i];
        const tbConfirmed = tb && Array.isArray(tb) &&
          ((tb[0] >= 10 && tb[0] - tb[1] >= 2) || (tb[1] >= 10 && tb[1] - tb[0] >= 2));
        return isSetCompleted(set, tbConfirmed) && set[1] > set[0];
      }).length;
      
      // Vérifier victoire (2 sets en BO3, 3 sets en BO5)
      const formatMatch = match.formatMatch || currentScore.formatMatch || "BEST_OF_3";
      const setsNeeded = formatMatch === "BEST_OF_5" ? 3 : 2;
      
      if (j1SetsWon >= setsNeeded || j2SetsWon >= setsNeeded) {
        return j1SetsWon > j2SetsWon ? "J1" : "J2";
      }
      return null;
    };

    // Ouvrir le modal de sélection
    const handleExportMatches = () => {
      setSelectedForExport([]);
      setShowExportModal(true);
    };

    // Export effectif des matchs sélectionnés
    const performExport = () => {
      try {
        if (selectedForExport.length === 0) {
          alert('❌ Veuillez sélectionner au moins un match');
          return;
        }

        const matchesToExport = [];
        
        selectedForExport.forEach(matchId => {
          const key = `match_${matchId}`;
          const matchData = JSON.parse(localStorage.getItem(key));
          if (matchData) matchesToExport.push(matchData);
        });
        
        const exportData = {
          exportDate: new Date().toISOString(),
          version: "1.0",
          matchCount: matchesToExport.length,
          matches: matchesToExport
        };
        
        const json = JSON.stringify(exportData, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `TenStats_Export_${new Date().toISOString().slice(0,10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        
        console.log(`✅ ${matchesToExport.length} match(s) exporté(s)`);
        alert(`✅ ${matchesToExport.length} match(s) exporté(s) avec succès !`);
        
        setShowExportModal(false);
        setSelectedForExport([]);
      } catch (error) {
        console.error('❌ Erreur export:', error);
        alert('❌ Erreur lors de l\'export');
      }
    };

    // Toggle sélection d'un match
    const toggleExportSelection = (matchId) => {
      setSelectedForExport(prev => {
        if (prev.includes(matchId)) {
          return prev.filter(id => id !== matchId);
        } else {
          return [...prev, matchId];
        }
      });
    };

    // Tout sélectionner
    const selectAllForExport = () => {
      setSelectedForExport(matches.map(m => m.matchId));
    };

    // Tout désélectionner
    const deselectAllForExport = () => {
      setSelectedForExport([]);
    };

    // Import des matchs depuis JSON
    const handleImportMatches = (event) => {
      const file = event.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const importData = JSON.parse(e.target.result);
          
          if (!importData.matches || !Array.isArray(importData.matches)) {
            alert('❌ Fichier invalide : format incorrect');
            return;
          }
          
          let imported = 0;
          let skipped = 0;
          
          importData.matches.forEach(matchData => {
            const key = `match_${matchData.matchId}`;
            
            if (localStorage.getItem(key)) {
              const replace = window.confirm(
                `Le match ${getPlayerFullName(matchData, "J1")} vs ${getPlayerFullName(matchData, "J2")} existe déjà. Remplacer ?`
              );
              if (!replace) {
                skipped++;
                return;
              }
            }
            
            localStorage.setItem(key, JSON.stringify(matchData));
            imported++;
          });
          
          // APRÈS
          const result = await getAllMatches();

          setMatches(result.matches);
          
          console.log(`✅ Import: ${imported} importé(s), ${skipped} ignoré(s)`);
          alert(`✅ Import terminé !\n${imported} match(s) importé(s)\n${skipped} match(s) ignoré(s)`);
          
          event.target.value = '';
        } catch (error) {
          console.error('❌ Erreur import:', error);
          alert('❌ Erreur lors de l\'import : fichier invalide');
        }
      };
      reader.readAsText(file);
    };

  // 🆕 Handler import Excel
  const handleExcelImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setExcelImporting(true);
    try {
      const result = await importMatchFromExcel(file, currentUser.userId);
      if (result.success) {
        const { matches: reloaded } = await getAllMatches();
        setMatches(reloaded);
        const setsDisplay = result.currentScore.sets
          .filter(s => s[0] > 0 || s[1] > 0)
          .map(s => s.join('-'))
          .join(' / ');
        alert(`✅ Import Excel réussi !\n${result.pointsCount} points importés\nScore : ${setsDisplay}`);
      } else {
        alert(`❌ Erreur import Excel : ${result.error}`);
      }
    } catch (err) {
      alert(`❌ Erreur : ${err.message}`);
    } finally {
      setExcelImporting(false);
      event.target.value = '';
    }
  };

  // 🆕 Afficher un loader pendant le chargement du profil
  if (subscriptionLoading) {
    return (
      <div style={{ 
        padding: "24px", 
        textAlign: "center", 
        fontFamily: "Arial, sans-serif" 
      }}>
        <div>Chargement de votre profil...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", fontFamily: "Arial, sans-serif" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
      
      
        <h2 style={{ margin: 0, fontSize: "20px" }}>
          Mes matchs
          {/* 🆕 Badge de rôle */}
          {role === 'expert' && <span style={{ marginLeft: '8px', fontSize: '16px' }}>⭐ Expert</span>}
          {role === 'admin' && <span style={{ marginLeft: '8px', fontSize: '16px' }}>👑 Admin</span>}
        </h2>
        <div style={{ display: "flex", gap: "2px" }}>
          {/* 🔧 Export/Import : DEV uniquement */}
          {process.env.REACT_APP_ENV === 'development' && (
            <>
              <button
                onClick={handleExportMatches}
                style={{
                  padding: "6px 6px",
                  borderRadius: "4px",
                  border: "none",
                  backgroundColor: "#4caf50",
                  color: "white",
                  fontSize: "13px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                Exporter
              </button>
              <label style={{
                padding: "6px 6px",
                borderRadius: "4px",
                border: "none",
                backgroundColor: "#FF9800",
                color: "white",
                fontSize: "13px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center"
              }}>
                Importer
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportMatches}
                  style={{ display: "none" }}
                />
              </label>
            </>
          )}

          {/* 🎬 Import Vidéo : Expert + Admin uniquement - masqué sur dev */}
          {/* 🆕 Import Excel : visible pour tous */}
          <label style={{
            padding: "6px 8px",
            borderRadius: "4px",
            border: "none",
            backgroundColor: excelImporting ? "#999" : "#2E7D32",
            color: "white",
            fontSize: "13px",
            fontWeight: "500",
            cursor: excelImporting ? "wait" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            opacity: excelImporting ? 0.7 : 1,
          }}>
            {excelImporting ? '⏳' : '📊'} Excel
            <input
              type="file"
              accept=".xlsx"
              onChange={handleExcelImport}
              style={{ display: "none" }}
              disabled={excelImporting}
            />
          </label>

          {/* 🎬 Import Vidéo : Expert + Admin uniquement - masqué sur dev */}
          {(role === 'admin') && (
            <button
              onClick={onNavigateVideos}
              style={{
                padding: "6px 8px",
                borderRadius: "4px",
                border: "none",
                backgroundColor: "#7B1FA2",
                color: "white",
                fontSize: "13px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              🎬 Vidéos
            </button>
          )}
          <button
            onClick={handleNewMatch}
            style={{
              padding: "6px 2px",
              borderRadius: "4px",
              border: "none",
              backgroundColor: canCreateMatch ? "#1976d2" : "#999",
              color: "white",
              fontSize: "13px",
              fontWeight: "500",
              cursor: "pointer",  // 🔧 Toujours pointer
              opacity: canCreateMatch ? 1 : 0.6,
              pointerEvents: "auto",  // 🔧 Force le clic
            }}
          >
            Nouveau match {isGratuit && `(${matchesRemaining} restants)`}
          </button>
        </div>
      </div>

      {/* 🆕 Compteur de matchs (gratuit uniquement) */}
      {isGratuit && (
        <div 
          onClick={() => matchesRemaining === 0 && setShowUpgradeModal(true)}  // 🔧 Cliquable si limite atteinte
          style={{
            padding: '12px 16px',
            backgroundColor: matchesRemaining <= 2 ? '#fff3e0' : '#e8f5e9',
            borderRadius: '8px',
            marginBottom: '16px',
            textAlign: 'center',
            border: `2px solid ${matchesRemaining <= 2 ? '#ff9800' : '#4caf50'}`,
            cursor: matchesRemaining === 0 ? 'pointer' : 'default',  // 🔧 Curseur pointer si 0
            transition: 'transform 0.2s, box-shadow 0.2s',  // 🔧 Animation
            ...(matchesRemaining === 0 && {
              ':hover': {
                transform: 'scale(1.02)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }
            })
          }}
          onMouseEnter={(e) => {
            if (matchesRemaining === 0) {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }
          }}
          onMouseLeave={(e) => {
            if (matchesRemaining === 0) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
        >
          <div style={{ 
            fontSize: '14px',
            color: matchesRemaining <= 2 ? '#e65100' : '#2e7d32',
            fontWeight: 'bold',
            marginBottom: '4px'
          }}>
            📊 Version Gratuite : {matchesRemaining} / {matchesLimit} matchs restants
          </div>
          
          {matchesRemaining <= 2 && (
            <div style={{ 
              fontSize: '12px', 
              color: '#666',
              marginTop: '4px'
            }}>
              {matchesRemaining === 0 
                ? '👆 Cliquez ici pour passer à NORMAL (7j d\'essai gratuit)' 
                : 'Passez à NORMAL pour des matchs illimités (7j d\'essai gratuit)'
              }
            </div>
          )}
        </div>
      )}

      {matches.length === 0 ? (
        <div style={{ color: "#777" }}>Aucun match pour l'instant.</div>
      ) : (
        <div
          style={{
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid #e0e0e0",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
            }}
          >
            <thead style={{ backgroundColor: "var(--bg-secondary)" }}>
              <tr>
                <th style={{ padding: "4px", textAlign: "left" }}>Date</th>
                <th style={{ padding: "2px", textAlign: "center" }}>Joueurs</th>
                <th colSpan="2" style={{ padding: "0px", textAlign: "left" }}>Score final</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => {
                const matchKey = m.id || m.matchId;
                
                // 🆕 Charger le score depuis le fichier individuel si disponible
                let currentScore = m.currentScore || null;
                let hasPoints = false;
                
                // Essayer de charger depuis le fichier individuel pour avoir le score actuel
                const storageKey = `match_${matchKey}`;
                const savedData = localStorage.getItem(storageKey);
                if (savedData) {
                  try {
                    const matchData = JSON.parse(savedData);
                    if (matchData.currentScore) {
                      currentScore = matchData.currentScore;
                    }
                    // Vérifier s'il y a des points
                    hasPoints = matchData.engineState?.points?.length > 0;
                  } catch (e) {
                    console.error('Erreur parsing match:', e);
                  }
                }
                
                const winner = getWinner(currentScore, m);
                
                return (
                  <tr key={matchKey}>
                    {/* Colonne 1 : Date */}
                    <td>{formatDate(m.dateMatch)}</td>
                    

                    {/* Colonne 2 : Joueurs avec winner en vert */}
                    <td style={{ textAlign: "left" }}>
                      <span style={{ 
                        fontWeight: winner === 'J1' ? 'bold' : 'normal',
                        color: winner === 'J1' ? '#4caf50' : 'inherit'
                      }}>
                        {getPlayerFullName(m, "J1")}
                      </span>
                      
                      {' vs '}
                      
                      <span style={{ 
                        fontWeight: winner === 'J2' ? 'bold' : 'normal',
                        color: winner === 'J2' ? '#4caf50' : 'inherit'
                      }}>
                        {getPlayerFullName(m, "J2")}
                      </span>
                    </td>
                    
                    {/* Colonne 3 : Score final */}
                    <td style={{ textAlign: "right", paddingRight: "2px", width: "1%", whiteSpace: "nowrap" }}>
                      {(() => {
                        // 🆕 Pas de points encore → "En cours"
                        if (!hasPoints) {
                          return <span style={{ color: '#999', fontStyle: 'italic' }}>En cours</span>;
                        }
                        
                        // Pas de score du tout
                        if (!currentScore || !currentScore.sets) {
                          return <span style={{ color: '#999', fontStyle: 'italic' }}>En cours</span>;
                        }
                        
                        // Filtrer les sets avec score
                        const validSets = currentScore.sets.filter(
                          set => set && Array.isArray(set) && (set[0] > 0 || set[1] > 0)
                        );
                        
                        // Aucun set avec score mais il y a des points → afficher le jeu en cours
                        if (validSets.length === 0 && currentScore.jeuScore) {
                          return (
                            <div style={{ color: '#2196f3', fontWeight: '500' }}>
                              {currentScore.jeuScore}
                            </div>
                          );
                        }
                        
                        // Aucun set avec score
                        if (validSets.length === 0) {
                          return <span style={{ color: '#999', fontStyle: 'italic' }}>0-0</span>;
                        }
                        
                        // Afficher les scores (un set par ligne) avec alignement grille
                        const tbScores = currentScore.lastTieBreakScores || [];
                        return (
                          <div style={{ fontSize: '13px', textAlign: 'right' }}>
                            {validSets.map((set, i) => {
                              const j1Won = set[0] > set[1];
                              const j2Won = set[1] > set[0];
                              const tb = tbScores[i];
                              const tbDisplay = (tb && Array.isArray(tb) && tb.length >= 2 && typeof tb[0] === 'number' && typeof tb[1] === 'number')
                                ? `${tb[0]}-${tb[1]}`
                                : null;
                              
                              return (
                                <div key={i} style={{ display: 'flex', alignItems: 'baseline', lineHeight: '1.3', whiteSpace: 'nowrap' }}>
                                  <span style={{ 
                                    fontWeight: (j1Won && winner === 'J1') ? 'bold' : 'normal',
                                    color: (j1Won && winner === 'J1') ? '#4caf50' : 'inherit'
                                  }}>
                                    {set[0]}
                                  </span>
                                  <span style={{ margin: '0 1px' }}>-</span>
                                  <span style={{ 
                                    fontWeight: (j2Won && winner === 'J2') ? 'bold' : 'normal',
                                    color: (j2Won && winner === 'J2') ? '#4caf50' : 'inherit'
                                  }}>
                                    {set[1]}
                                  </span>
                                  {tbDisplay && (
                                    <span style={{ fontSize: '11px', color: '#888', marginLeft: '3px' }}>
                                      ({tbDisplay})
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                            
                            {/* 🆕 Indication d'abandon */}
                            {currentScore.abandonWinner && (
                              <div style={{ 
                                marginTop: '4px',
                                fontSize: '12px',
                                color: '#ff5722',
                                fontWeight: 'bold',
                                fontStyle: 'italic'
                              }}>
                                Ab.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    
                    {/* Colonne 4 : Actions (boutons) */}
                    <td style={{ padding: "8px", borderTop: "1px solid #eee", textAlign: "right" }}>
                      <div style={{ 
                        display: "grid", 
                        gridTemplateColumns: "32px 32px",
                        gap: "4px",
                        justifyContent: "flex-end"
                      }}>
                        {/* Bouton Ouvrir */}
                        <button
                          onClick={() => onOpenMatch(m)}
                          title="Ouvrir le match"
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: "0",
                            borderRadius: "4px",
                            border: "none",
                            backgroundColor: "#4caf50",
                            color: "white",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          ▶️
                        </button>
                        
                        {/* Bouton Modifier */}
                        <button
                          onClick={() => handleEditMatch(m)}
                          title="Modifier"
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: "0",
                            borderRadius: "4px",
                            border: "none",
                            backgroundColor: "#2196f3",
                            color: "white",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          ✏️
                        </button>
                        
                        {/* Bouton Stats */}
                        <button
                          onClick={() => onViewStats(m)}
                          title="Voir les statistiques"
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: "0",
                            borderRadius: "4px",
                            border: "none",
                            backgroundColor: "#ff9800",
                            color: "white",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          📊
                        </button>
                        
                        {/* Bouton Supprimer */}
                        <button
                          onClick={() => setDeleteConfirm({ 
                            id: m.id || m.matchId,
                            matchId: m.matchId,
                            playerNames: `${getPlayerFullName(m, "J1")} vs ${getPlayerFullName(m, "J2")}` 
                          })}
                          title="Supprimer"
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: "0",
                            borderRadius: "4px",
                            border: "none",
                            backgroundColor: "#f44336",
                            color: "white",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
        </table>
        </div>
      )}

      {deleteConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: "20px", color: "#333" }}>
              Supprimer le match ?
            </h3>
            <p style={{ margin: "0 0 24px 0", color: "#666", lineHeight: "1.5" }}>
              {deleteConfirm.playerNames}
              <br />
              <strong>Cette action est irréversible.</strong>
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  backgroundColor: "white",
                  color: "#333",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600"
                }}
              >
                Annuler
              </button>
              <button
                onClick={() => handleDeleteMatch(deleteConfirm.id || deleteConfirm.matchId)}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#f44336",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600"
                }}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de sélection pour export */}
      {showExportModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
          onClick={() => setShowExportModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: "20px", color: "#333" }}>
              Sélectionner les matchs à exporter
            </h3>
            
            <div style={{ marginBottom: "16px", display: "flex", gap: "8px" }}>
              <button
                onClick={selectAllForExport}
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  backgroundColor: "white",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Tout
              </button>
              <button
                onClick={deselectAllForExport}
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  backgroundColor: "white",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Aucun
              </button>
              {selectedForExport.length > 0 && (
                <span style={{ fontSize: "13px", color: "#666", alignSelf: "center" }}>
                  {selectedForExport.length} sélectionné{selectedForExport.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div style={{ 
              overflowY: "auto", 
              marginBottom: "16px", 
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              maxHeight: "400px"
            }}>
              {matches.map((m) => (
                <label
                  key={m.matchId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "12px",
                    borderBottom: "1px solid #f0f0f0",
                    cursor: "pointer",
                    backgroundColor: selectedForExport.includes(m.matchId) ? "#f0f8ff" : "white"
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedForExport.includes(m.matchId)}
                    onChange={() => toggleExportSelection(m.matchId)}
                    style={{ marginRight: "12px", cursor: "pointer" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", fontSize: "14px" }}>
                      {getPlayerFullName(m, "J1")} vs {getPlayerFullName(m, "J2")}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                      {m.dateMatch} • {m.scoreFinal || "En cours"}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowExportModal(false)}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  backgroundColor: "white",
                  color: "#333",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600"
                }}
              >
                Annuler
              </button>
              <button
                onClick={performExport}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#4caf50",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600"
                }}
              >
                Exporter ({selectedForExport.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal d'upgrade */}
      {showUpgradeModal && (
        <UpgradeModal 
          onClose={() => setShowUpgradeModal(false)} 
          matchesLimit={matchesLimit}  // 🔧 Passer la limite dynamique
        />
      )}
    </div>
  )
}

export default MatchListPage;